/**
 * EF Gallery — Swiper-powered product gallery (2026-05-01)
 *
 * Replaces the hand-rolled `efArrow / efSwitchImage / efSwitchVideo` flow
 * with Swiper 11. Same library Glozin uses on the live theme — battle-tested
 * touch behaviour for Samsung Internet, mouse-drag, mobile momentum scroll,
 * and `<model-viewer>` (GLB) compatibility.
 *
 * Two synced instances:
 *   #efGallerySwiper  — main slider (full-size media)
 *   #efGalleryThumbs  — thumbnails (slideToClickedSlide + watchSlidesProgress)
 *
 * Swiper handles all touch/drag/keyboard events, plus thumbs↔main sync.
 * Custom slideChange hook updates the "1 / N" counter and auto-plays/pauses
 * the active slide's <video>.
 */
(function () {
  'use strict';

  var INIT_TIMEOUT_MS = 6000;
  var POLL_INTERVAL_MS = 80;

  function init() {
    var main = document.getElementById('efGallerySwiper');
    if (!main) return;
    var thumbs = document.getElementById('efGalleryThumbs');

    var thumbsSwiper = null;
    if (thumbs) {
      thumbsSwiper = new Swiper(thumbs, {
        /* slidesPerView:'auto' (not a fixed 5) so maxTranslate = the TRUE content end.
           With a fixed integer slidesPerView Swiper snap-aligns maxTranslate to the
           (count - perView)th slide, which on long galleries (e.g. 16-image standard
           doors) left the last ~144px of the final thumbnail cut off — "doesn't reach
           the end". Thumb width is a fixed px set in ef-gallery-shared.css for .auto mode.
           freeMode lets the rail glide to that true end. 2026-06-17 */
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        freeMode: { enabled: true, momentum: true, sticky: false },
        watchOverflow: true
      });
    }

    /* Pre-init inline `display:none` guards on non-first slides (set in
       template) — strip them BEFORE new Swiper() so Swiper sees full slide
       set. After init Swiper manages visibility itself. 2026-05-04. */
    main.querySelectorAll('.ef-pd-slide--preinit-hidden').forEach(function (s) {
      s.style.display = '';
      s.classList.remove('ef-pd-slide--preinit-hidden');
    });
    /* Same for thumbnail rail */
    if (thumbs) {
      thumbs.querySelectorAll('.ef-pd-thumb--preinit-hidden').forEach(function (t) {
        t.style.display = '';
        t.classList.remove('ef-pd-thumb--preinit-hidden');
      });
    }
    /* Thumb wrap was visibility:hidden via inline style — reveal it now */
    var thumbWrap = document.getElementById('efThumbWrap');
    if (thumbWrap) thumbWrap.style.visibility = '';

    var mainSwiper = new Swiper(main, {
      slidesPerView: 1,
      speed: 400,
      threshold: 5,
      resistanceRatio: 0.85,
      grabCursor: true,
      /* 2026-05-31 — recalc slide width/translate when the container or its
         parents resize (e.g. a <model-viewer>/GLB loads and reflows the column).
         Without this Swiper kept a stale width → the active slide rendered
         narrow + shifted left on mobile. */
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      resizeObserver: true,
      keyboard: { enabled: true, onlyInViewport: true },
      navigation: {
        prevEl: main.querySelector('.swiper-button-prev'),
        nextEl: main.querySelector('.swiper-button-next')
      },
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
      /* Critical for <model-viewer> + <video> slides — Swiper must NOT swallow
         touch events that originate inside those interactive elements, otherwise
         GLB rotation and video controls break. */
      noSwipingSelector: 'model-viewer, video, button, a',
      on: {
        slideChange: function () {
          var idx = this.activeIndex;
          var counter = document.getElementById('ef-pd-counter-cur');
          if (counter) counter.textContent = (idx + 1).toString();
          // Pause all videos in gallery
          main.querySelectorAll('video').forEach(function (v) {
            try { v.pause(); } catch (e) {}
          });
          // Auto-play active slide if it's a video
          var slide = this.slides[idx];
          // Official Real Fyre embeds stop when leaving their gallery slide.
          main.querySelectorAll('[data-ef-rf-video] iframe').forEach(function (frame) {
            if (slide && slide.contains(frame)) return;
            try {
              if (new URL(frame.src).hostname === 'player.vimeo.com') {
                frame.contentWindow.postMessage({ method: 'pause' }, 'https://player.vimeo.com');
              } else if (/^(www\.)?youtube(?:-nocookie)?\.com$/.test(new URL(frame.src).hostname)) {
                frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), new URL(frame.src).origin);
              }
            } catch (e) {}
          });
          if (slide && slide.dataset.mediaType === 'video') {
            var v = slide.querySelector('video');
            if (v) {
              try { v.muted = true; v.loop = true; v.play(); } catch (e) {}
            }
          }
        }
      }
    });

    /* Blur arrow buttons after click so the gallery's :focus-within doesn't
       keep them visible after the mouse leaves. CSS uses :focus-visible for
       keyboard users, so this only neutralizes the mouse-click focus. */
    main.querySelectorAll('.swiper-button-prev, .swiper-button-next, .ef-pd-zoom-btn').forEach(function (btn) {
      btn.addEventListener('mouseup', function () {
        try { btn.blur(); } catch (e) {}
      });
    });

    /* Force a recalc after first paint, on full load, and once any <model-viewer>
       reports load — covers the case where GLB/late media reflows the column and
       leaves the active slide narrow/shifted (2026-05-31). */
    /* Recalc size AND re-seat the active slide. update() alone fixed the slide
       WIDTH but left the wrapper translate stale → active slide parked ~24px left
       of its column (bled off the left edge, gap on the right). slideTo() re-snaps
       the translate to the correct position. 2026-05-31. */
    function efGalleryReflow() {
      try {
        mainSwiper.updateSize();
        mainSwiper.updateSlides();
        mainSwiper.update();
        mainSwiper.slideTo(mainSwiper.activeIndex || 0, 0, false);
        if (thumbsSwiper) { thumbsSwiper.update(); }
      } catch (e) {}
    }
    [80, 250, 600, 1200, 2000].forEach(function (ms) { setTimeout(efGalleryReflow, ms); });
    window.addEventListener('load', efGalleryReflow);
    window.addEventListener('resize', efGalleryReflow);
    main.querySelectorAll('model-viewer').forEach(function (mv) {
      mv.addEventListener('load', efGalleryReflow);
      mv.addEventListener('poster-dismissed', efGalleryReflow);
    });

    // Auto-play first slide if it's a video (mirrors mantel/doors v1 behaviour)
    setTimeout(function () {
      var first = main.querySelector('.swiper-slide-active');
      if (first && first.dataset.mediaType === 'video') {
        var v = first.querySelector('video');
        if (v) {
          try { v.muted = true; v.loop = true; v.play(); } catch (e) {}
        }
      }
    }, 120);

    /* ─────────────────────────────────────────────────────────
       Zoom button — opens existing #ef-pd-lightbox at active slide.
       Hidden for non-image slides (video / model).
       ─────────────────────────────────────────────────────── */
    var zoomBtn = document.getElementById('efGalleryZoom');
    if (zoomBtn) {
      function syncZoomVisibility() {
        var slide = main.querySelectorAll('.swiper-slide')[mainSwiper.activeIndex];
        var t = slide && slide.dataset.mediaType;
        zoomBtn.hidden = (t !== 'image');
      }
      mainSwiper.on('slideChange', syncZoomVisibility);
      syncZoomVisibility();

      zoomBtn.addEventListener('click', function () {
        var slide = main.querySelectorAll('.swiper-slide')[mainSwiper.activeIndex];
        if (!slide) return;
        var img = slide.querySelector('img');
        if (!img) return;
        openLightbox(img.currentSrc || img.src, img.alt, mainSwiper.activeIndex);
      });
    }

    function openLightbox(src, alt, idx) {
      var lb = document.getElementById('ef-pd-lightbox');
      var lbImg = document.getElementById('ef-lb-img');
      var lbCounter = document.getElementById('ef-lb-counter');
      if (!lb || !lbImg) return;
      // Use highest-res src — slide img has srcset at 1200w
      var slide = main.querySelectorAll('.swiper-slide')[idx];
      var imgEl = slide && slide.querySelector('img');
      // Prefer the largest entry in srcset
      var bestSrc = src;
      if (imgEl && imgEl.srcset) {
        var entries = imgEl.srcset.split(',').map(function (s) {
          var parts = s.trim().split(' ');
          return { url: parts[0], w: parseInt(parts[1] || '0', 10) };
        });
        entries.sort(function (a, b) { return b.w - a.w; });
        if (entries[0]) bestSrc = entries[0].url;
      }
      lbImg.src = bestSrc;
      lbImg.alt = alt || '';
      // Count image-only slides for the counter
      var imageSlides = Array.prototype.filter.call(main.querySelectorAll('.swiper-slide'), function (s) {
        return s.dataset.mediaType === 'image';
      });
      var imageIdx = imageSlides.indexOf(slide);
      if (lbCounter) lbCounter.textContent = (imageIdx + 1) + ' / ' + imageSlides.length;
      lb.classList.add('ef-pd-lightbox--open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      var lb = document.getElementById('ef-pd-lightbox');
      if (!lb) return;
      lb.classList.remove('ef-pd-lightbox--open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    var lbCloseBtn = document.getElementById('ef-lb-close');
    if (lbCloseBtn) lbCloseBtn.addEventListener('click', closeLightbox);
    var lb = document.getElementById('ef-pd-lightbox');
    if (lb) lb.addEventListener('click', function (e) { if (e.target === lb) closeLightbox(); });
    document.addEventListener('keydown', function (e) {
      var lbEl = document.getElementById('ef-pd-lightbox');
      if (!lbEl || !lbEl.classList.contains('ef-pd-lightbox--open')) return;
      if (e.key === 'Escape') closeLightbox();
    });

    // Expose for debugging / 3rd-party hooks
    window.EFGallery = { main: mainSwiper, thumbs: thumbsSwiper, openLightbox: openLightbox, closeLightbox: closeLightbox };
  }

  function start() {
    if (typeof Swiper !== 'undefined') {
      init();
      return;
    }
    // Swiper not yet ready — poll briefly while widget.js loads
    var deadline = Date.now() + INIT_TIMEOUT_MS;
    var iv = setInterval(function () {
      if (typeof Swiper !== 'undefined') {
        clearInterval(iv);
        init();
      } else if (Date.now() > deadline) {
        clearInterval(iv);
        console.warn('[ef-gallery-swiper] Swiper failed to load');
      }
    }, POLL_INTERVAL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* EF_MANTEL_VARIANT_MODELS_20260911: only newly added mantel models; keep photographs and existing models unchanged. */
(function () {
  'use strict';
  var files = {"42429255778386":"ef-cabin-pine-maduro-72in-20260911-923639b33c.glb","42429255745618":"ef-cabin-pine-maduro-60in-20260911-f7ccc0d4d8.glb","42429255843922":"ef-cabin-pine-roasted-chestnut-72in-20260911-8a801a40db.glb","42429255811154":"ef-cabin-pine-roasted-chestnut-60in-20260911-03ce6b0fa5.glb","42429255909458":"ef-cabin-pine-pale-honey-72in-20260911-909bccb3a3.glb","42429255876690":"ef-cabin-pine-pale-honey-60in-20260911-5d55d13ffe.glb","42429256007762":"ef-cabin-pine-eclipse-72in-20260911-9a8f7008f8.glb","42429255974994":"ef-cabin-pine-eclipse-60in-20260911-47565e781d.glb","42429256106066":"ef-cabin-pine-weathered-grey-72in-20260911-4a53937298.glb","42429256073298":"ef-cabin-pine-weathered-grey-60in-20260911-2a30c356c5.glb","42282492035154":"ef-hayloft-pale-honey-72in-20260911-627b600ad3.glb","42282492002386":"ef-hayloft-pale-honey-60in-20260911-507f105293.glb","42282491969618":"ef-hayloft-pale-honey-48in-20260911-82d73f35fe.glb","42352945201234":"ef-hayloft-maduro-72in-20260911-bdc139be89.glb","42352945168466":"ef-hayloft-maduro-60in-20260911-9516bd00b2.glb","42352945135698":"ef-hayloft-maduro-48in-20260911-2458a9ca76.glb","42352946151506":"ef-hayloft-eclipse-72in-20260911-b1e727e979.glb","42352946118738":"ef-hayloft-eclipse-60in-20260911-0e7c4fe918.glb","42352946085970":"ef-hayloft-eclipse-48in-20260911-1a0f921f33.glb","42352946511954":"ef-hayloft-weathered-grey-72in-20260911-2d6138e6d8.glb","42352946479186":"ef-hayloft-weathered-grey-60in-20260911-2f43576cb2.glb","42352946446418":"ef-hayloft-weathered-grey-48in-20260911-439dab9272.glb","42352946970706":"ef-hayloft-roasted-chestnut-72in-20260911-5c9d085ea0.glb","42352946937938":"ef-hayloft-roasted-chestnut-60in-20260911-c2b6bb9bb3.glb","42352946905170":"ef-hayloft-roasted-chestnut-48in-20260911-ce68a21b98.glb","42156654690386":"ef-grist-mill-pale-honey-72in-20260911-ac62359e58.glb","42156654657618":"ef-grist-mill-pale-honey-60in-20260911-833ee1505d.glb","42156654723154":"ef-grist-mill-pale-honey-48in-20260911-e8b7d20318.glb","42172391587922":"ef-grist-mill-maduro-72in-20260911-54034d03bf.glb","42172391555154":"ef-grist-mill-maduro-60in-20260911-e6265abec6.glb","42172391522386":"ef-grist-mill-maduro-48in-20260911-6ae7354963.glb","42172395618386":"ef-grist-mill-eclipse-72in-20260911-5d079386b5.glb","42172395585618":"ef-grist-mill-eclipse-60in-20260911-be26db6a19.glb","42172395552850":"ef-grist-mill-eclipse-48in-20260911-75f304b013.glb","42280996110418":"ef-grist-mill-weathered-grey-72in-20260911-4e81b7521a.glb","42280996077650":"ef-grist-mill-weathered-grey-60in-20260911-66e5966874.glb","42280996044882":"ef-grist-mill-weathered-grey-48in-20260911-58bb46ca44.glb","42280999321682":"ef-grist-mill-roasted-chestnut-72in-20260911-828348d238.glb","42280999288914":"ef-grist-mill-roasted-chestnut-60in-20260911-12e146015a.glb","42280999256146":"ef-grist-mill-roasted-chestnut-48in-20260911-eec1dc7b94.glb","41189678907474":"ef-tobacco-barn-eclipse-72in-20260911-3b776adc87.glb","41189678874706":"ef-tobacco-barn-eclipse-60in-20260911-06f2a97269.glb","41189680775250":"ef-tobacco-barn-maduro-72in-20260911-7bfd66a7cc.glb","41189680742482":"ef-tobacco-barn-maduro-60in-20260911-1de21e14fd.glb","42352053682258":"ef-tobacco-barn-roasted-chestnut-72in-20260911-e3bdd9b8f8.glb","42352053649490":"ef-tobacco-barn-roasted-chestnut-60in-20260911-7bca81dcf0.glb","42352053616722":"ef-tobacco-barn-roasted-chestnut-48in-20260911-3b7e95b2e8.glb","42352972431442":"ef-rockface-stone-bluegrass-fog-72in-20260911-afb0da6b87.glb","42352972398674":"ef-rockface-stone-bluegrass-fog-60in-20260911-09210b7b62.glb","42352974594130":"ef-rockface-stone-turfway-tan-72in-20260911-266f1e13b1.glb","42352974561362":"ef-rockface-stone-turfway-tan-60in-20260911-0d48fd6d39.glb","41189462638674":"ef-rickhouse-pale-honey-72in-20260911-6c4580d117.glb","41189462605906":"ef-rickhouse-pale-honey-60in-20260911-c78e2ab866.glb","41189469388882":"ef-rickhouse-eclipse-72in-20260911-2489bc5416.glb","41189469356114":"ef-rickhouse-eclipse-60in-20260911-08a0c99eb9.glb","41189593153618":"ef-rickhouse-maduro-72in-20260911-b2dd9c47f2.glb","41189593120850":"ef-rickhouse-maduro-60in-20260911-cdb983de4a.glb","41189597872210":"ef-rickhouse-weathered-grey-72in-20260911-3394ae4d35.glb","41189597839442":"ef-rickhouse-weathered-grey-60in-20260911-e093fefe9a.glb","42352988422226":"ef-half-round-log-pale-honey-72in-20260911-a8d8352b38.glb","42352988389458":"ef-half-round-log-pale-honey-60in-20260911-8b5c2894de.glb","42352991338578":"ef-half-round-log-pale-honey-48in-20260911-2aa659fa63.glb","42352991862866":"ef-half-round-log-maduro-72in-20260911-9cdebca932.glb","42352991830098":"ef-half-round-log-maduro-60in-20260911-de1fc4a318.glb","42352991797330":"ef-half-round-log-maduro-48in-20260911-b76f462f20.glb","42352992747602":"ef-half-round-log-weathered-grey-72in-20260911-de04e886b9.glb","42352992714834":"ef-half-round-log-weathered-grey-60in-20260911-ca291d1cb7.glb","42352992682066":"ef-half-round-log-weathered-grey-48in-20260911-7849d57b09.glb","42353028431954":"ef-half-round-log-roasted-chestnut-72in-20260911-42c3fdcba7.glb","42353028399186":"ef-half-round-log-roasted-chestnut-60in-20260911-b01c690d25.glb","42353028366418":"ef-half-round-log-roasted-chestnut-48in-20260911-c00283e557.glb","42093235339346":"ef-farmhouse-pale-honey-84in-20260911-ef747ad283.glb","42093235306578":"ef-farmhouse-pale-honey-72in-20260911-b663b2893f.glb","42093272858706":"ef-farmhouse-maduro-84in-20260911-c7f977c322.glb","42093272825938":"ef-farmhouse-maduro-72in-20260911-a6c37d4aff.glb","42093275119698":"ef-farmhouse-weathered-grey-84in-20260911-f01ceae135.glb","42093275086930":"ef-farmhouse-weathered-grey-72in-20260911-b9e57eba43.glb","42093281968210":"ef-farmhouse-eclipse-84in-20260911-4ba33cfede.glb","42093281935442":"ef-farmhouse-eclipse-72in-20260911-28a7937b11.glb","42093282361426":"ef-farmhouse-roasted-chestnut-84in-20260911-74d49c8874.glb","42093282328658":"ef-farmhouse-roasted-chestnut-72in-20260911-595dd30e29.glb"};
  function syncWhenViewing3D() {
    var input = document.getElementById('efUniVariantId'), gallery = document.getElementById('efGallerySwiper');
    var file = input && files[input.value];
    if (!file || !gallery || !gallery.querySelector('.swiper-slide-active model-viewer')) return;
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('.swiper-slide'));
    var index = slides.findIndex(function (slide) { var model = slide.querySelector('model-viewer'); return model && (model.getAttribute('src') || '').split('?')[0].endsWith('/' + file); });
    if (index < 0 || !window.EFGallery || !window.EFGallery.main) return;
    var model = slides[index].querySelector('model-viewer'); model.setAttribute('reveal', 'auto');
    window.EFGallery.main.slideTo(index);
  }
  document.addEventListener('click', function (event) { if (event.target.closest('[data-variant-option]')) requestAnimationFrame(syncWhenViewing3D); });
  document.addEventListener('change', function (event) { if (event.target.closest('.ef-uni-variant-select')) requestAnimationFrame(syncWhenViewing3D); });
})();
